function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classPrivateFieldGet(receiver, privateMap) { var descriptor = privateMap.get(receiver); if (!descriptor) { throw new TypeError("attempted to get private field on non-instance"); } if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }

function _classPrivateFieldSet(receiver, privateMap, value) { var descriptor = privateMap.get(receiver); if (!descriptor) { throw new TypeError("attempted to set private field on non-instance"); } if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } return value; }

import { Histogram } from "./histogram.js";
const SIGBITS = 5;
const RSHIFT = 8 - SIGBITS;

var _volume = new WeakMap();

var _avg = new WeakMap();

var _count = new WeakMap();

export class VBox {
  static build(pixels) {
    const h = new Histogram(pixels, {
      sigBits: SIGBITS
    });
    const {
      rmin,
      rmax,
      gmin,
      gmax,
      bmin,
      bmax
    } = h;
    return new VBox(rmin, rmax, gmin, gmax, bmin, bmax, h);
  }

  constructor(r1, r2, g1, g2, b1, b2, histogram) {
    _defineProperty(this, "dimension", void 0);

    _defineProperty(this, "histogram", void 0);

    _volume.set(this, {
      writable: true,
      value: -1
    });

    _avg.set(this, {
      writable: true,
      value: null
    });

    _count.set(this, {
      writable: true,
      value: -1
    });

    // NOTE: dimension will be mutated by split operation.
    //       It must be specified explicitly, not from histogram
    this.dimension = {
      r1,
      r2,
      g1,
      g2,
      b1,
      b2
    };
    this.histogram = histogram;
  }

  invalidate() {
    _classPrivateFieldSet(this, _volume, -1);

    _classPrivateFieldSet(this, _count, -1);

    _classPrivateFieldSet(this, _avg, null);
  }

  volume() {
    if (_classPrivateFieldGet(this, _volume) < 0) {
      let {
        r1,
        r2,
        g1,
        g2,
        b1,
        b2
      } = this.dimension;

      _classPrivateFieldSet(this, _volume, (r2 - r1 + 1) * (g2 - g1 + 1) * (b2 - b1 + 1));
    }

    return _classPrivateFieldGet(this, _volume);
  }

  count() {
    if (_classPrivateFieldGet(this, _count) < 0) {
      let {
        histogram: {
          hist,
          getColorIndex
        },
        dimension: {
          r1,
          r2,
          g1,
          g2,
          b1,
          b2
        }
      } = this;
      let c = 0;

      for (let r = r1; r <= r2; r++) {
        for (let g = g1; g <= g2; g++) {
          for (let b = b1; b <= b2; b++) {
            c += hist[getColorIndex(r, g, b)];
          }
        }
      }

      _classPrivateFieldSet(this, _count, c);
    }

    return _classPrivateFieldGet(this, _count);
  }

  clone() {
    const {
      dimension: {
        r1,
        r2,
        g1,
        g2,
        b1,
        b2
      },
      histogram
    } = this;
    return new VBox(r1, r2, g1, g2, b1, b2, histogram);
  }

  avg() {
    if (!_classPrivateFieldGet(this, _avg)) {
      let {
        hist,
        getColorIndex
      } = this.histogram;
      let {
        r1,
        r2,
        g1,
        g2,
        b1,
        b2
      } = this.dimension;
      let [ntot, mult, rsum, gsum, bsum] = [0, 1 << 8 - SIGBITS, 0, 0, 0];

      for (let r = r1; r <= r2; r++) {
        for (let g = g1; g <= g2; g++) {
          for (let b = b1; b <= b2; b++) {
            const h = hist[getColorIndex(r, g, b)];
            ntot += h;
            rsum += h * (r + 0.5) * mult;
            gsum += h * (g + 0.5) * mult;
            bsum += h * (b + 0.5) * mult;
          }
        }
      }

      if (ntot) {
        _classPrivateFieldSet(this, _avg, [~~(rsum / ntot), ~~(gsum / ntot), ~~(bsum / ntot)]);
      } else {
        _classPrivateFieldSet(this, _avg, [~~(mult * (r1 + r2 + 1) / 2), ~~(mult * (g1 + g2 + 1) / 2), ~~(mult * (b1 + b2 + 1) / 2)]);
      }
    }

    return _classPrivateFieldGet(this, _avg);
  }

  contains(rgb) {
    let [r, g, b] = rgb;
    const {
      r1,
      r2,
      g1,
      g2,
      b1,
      b2
    } = this.dimension;
    r >>= RSHIFT;
    g >>= RSHIFT;
    b >>= RSHIFT;
    return r >= r1 && r <= r2 && g >= g1 && g <= g2 && b >= b1 && b <= b2;
  }

  split() {
    let {
      hist,
      getColorIndex
    } = this.histogram;
    let {
      r1,
      r2,
      g1,
      g2,
      b1,
      b2
    } = this.dimension;
    let count = this.count();
    if (!count) return [];
    if (count === 1) return [this.clone()];
    let [rw, gw, bw] = [r2 - r1 + 1, g2 - g1 + 1, b2 - b1 + 1];
    const maxw = Math.max(rw, gw, bw);
    let accSum = null;
    let [sum, total] = [0, 0];
    let maxd = null;

    if (maxw === rw) {
      maxd = `r`;
      accSum = new Uint32Array(r2 + 1);

      for (let r = r1; r <= r2; r++) {
        sum = 0;

        for (let g = g1; g <= g2; g++) {
          for (let b = b1; b <= b2; b++) {
            let index = getColorIndex(r, g, b);
            sum += hist[index];
          }
        }

        total += sum;
        accSum[r] = total;
      }
    } else if (maxw === gw) {
      maxd = `g`;
      accSum = new Uint32Array(g2 + 1);

      for (let g = g1; g <= g2; g++) {
        sum = 0;

        for (let r = r1; r <= r2; r++) {
          for (let b = b1; b <= b2; b++) {
            let index = getColorIndex(r, g, b);
            sum += hist[index];
          }
        }

        total += sum;
        accSum[g] = total;
      }
    } else {
      maxd = `b`;
      accSum = new Uint32Array(b2 + 1);

      for (let b = b1; b <= b2; b++) {
        sum = 0;

        for (let r = r1; r <= r2; r++) {
          for (let g = g1; g <= g2; g++) {
            let index = getColorIndex(r, g, b);
            sum += hist[index];
          }
        }

        total += sum;
        accSum[b] = total;
      }
    }

    let splitPoint = -1;
    const reverseSum = new Uint32Array(accSum.length);

    for (let i = 0; i < accSum.length; i++) {
      let d = accSum[i];
      if (splitPoint < 0 && d > total / 2) splitPoint = i;
      reverseSum[i] = total - d;
    }

    const doCut = d => {
      const dim1 = `${d}1`;
      const dim2 = `${d}2`;
      let d1 = this.dimension[dim1];
      let d2 = this.dimension[dim2];
      const vbox1 = this.clone();
      const vbox2 = this.clone();
      const left = splitPoint - d1;
      const right = d2 - splitPoint;

      if (left <= right) {
        d2 = Math.min(d2 - 1, ~~(splitPoint + right / 2));
        d2 = Math.max(0, d2);
      } else {
        d2 = Math.max(d1, ~~(splitPoint - 1 - left / 2));
        d2 = Math.min(this.dimension[dim2], d2);
      }

      while (!accSum[d2]) d2++;

      let c2 = reverseSum[d2];

      while (!c2 && accSum[d2 - 1]) c2 = reverseSum[--d2];

      vbox1.dimension[dim2] = d2;
      vbox2.dimension[dim1] = d2 + 1;
      return [vbox1, vbox2];
    };

    return doCut(maxd);
  }

}